import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { 
  Banknote, DollarSign, CheckCircle2, XCircle, Clock, 
  Eye, Send, User, Building, RefreshCw
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentWithdrawals() {
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Detail view state
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Process state
  const [processing, setProcessing] = useState(false);
  const [processForm, setProcessForm] = useState({
    status: "",
    admin_notes: "",
    transaction_reference: ""
  });
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
  const [bulkPaidDialogOpen, setBulkPaidDialogOpen] = useState(false);
  const [bulkNotes, setBulkNotes] = useState("");
  const [bulkTransactionRef, setBulkTransactionRef] = useState("");

  useEffect(() => {
    fetchWithdrawals();
    fetchStats();
  }, [statusFilter]);

  const fetchWithdrawals = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const url = statusFilter === "all" 
        ? `${API}/agent/withdrawals`
        : `${API}/agent/withdrawals?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(response.data);
    } catch (error) {
      toast.error("Failed to load withdrawals");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/withdrawals/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.log("Failed to load stats");
    }
  };

  const viewWithdrawalDetails = async (withdrawalId) => {
    setDetailLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/withdrawals/${withdrawalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedWithdrawal(response.data);
      setProcessForm({
        status: "",
        admin_notes: "",
        transaction_reference: ""
      });
    } catch (error) {
      toast.error("Failed to load withdrawal details");
    } finally {
      setDetailLoading(false);
    }
  };

  const processWithdrawal = async () => {
    if (!processForm.status) {
      toast.error("Please select an action");
      return;
    }
    
    setProcessing(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(
        `${API}/agent/withdrawals/${selectedWithdrawal.withdrawal_id}/status`,
        processForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Withdrawal ${processForm.status} successfully`);
      setSelectedWithdrawal(null);
      fetchWithdrawals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process withdrawal");
    } finally {
      setProcessing(false);
    }
  };

  // Bulk selection handlers
  const pendingWithdrawals = withdrawals.filter(w => w.status === "pending");
  const approvedWithdrawals = withdrawals.filter(w => w.status === "approved");
  
  const toggleSelectAll = () => {
    const selectableIds = withdrawals
      .filter(w => w.status === "pending" || w.status === "approved")
      .map(w => w.withdrawal_id);
    
    if (selectedIds.length === selectableIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  };

  const toggleSelect = (withdrawalId) => {
    setSelectedIds(prev => 
      prev.includes(withdrawalId) 
        ? prev.filter(id => id !== withdrawalId)
        : [...prev, withdrawalId]
    );
  };

  const isSomeSelected = selectedIds.length > 0;
  
  // Get selected withdrawals by status
  const selectedPending = selectedIds.filter(id => 
    withdrawals.find(w => w.withdrawal_id === id)?.status === "pending"
  );
  const selectedApproved = selectedIds.filter(id => 
    withdrawals.find(w => w.withdrawal_id === id)?.status === "approved"
  );

  // Bulk actions
  const handleBulkApprove = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/withdrawals/bulk`,
        { ids: selectedPending, action: "approve", params: { admin_notes: bulkNotes } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Approved ${response.data.success} withdrawals`);
      setBulkApproveDialogOpen(false);
      setBulkNotes("");
      setSelectedIds([]);
      fetchWithdrawals();
      fetchStats();
    } catch (error) {
      toast.error("Failed to approve withdrawals");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/withdrawals/bulk`,
        { ids: selectedPending, action: "reject", params: { admin_notes: bulkNotes || "Request rejected" } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Rejected ${response.data.success} withdrawals`);
      setBulkRejectDialogOpen(false);
      setBulkNotes("");
      setSelectedIds([]);
      fetchWithdrawals();
      fetchStats();
    } catch (error) {
      toast.error("Failed to reject withdrawals");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkMarkPaid = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/withdrawals/bulk`,
        { ids: selectedApproved, action: "mark_paid", params: { transaction_reference: bulkTransactionRef } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Marked ${response.data.success} withdrawals as paid`);
      setBulkPaidDialogOpen(false);
      setBulkTransactionRef("");
      setSelectedIds([]);
      fetchWithdrawals();
      fetchStats();
    } catch (error) {
      toast.error("Failed to mark as paid");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-blue-100 text-blue-800",
      paid: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="h-4 w-4 text-yellow-600" />,
      approved: <CheckCircle2 className="h-4 w-4 text-blue-600" />,
      paid: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      rejected: <XCircle className="h-4 w-4 text-red-600" />
    };
    return icons[status] || <Clock className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          <Banknote className="inline h-8 w-8 mr-2 text-primary" />
          Withdrawal Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Review and process customer withdrawal requests
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="dashboard-card bg-yellow-50 border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Clock className="h-8 w-8 text-yellow-600" />
                <div>
                  <p className="text-2xl font-bold text-yellow-800">{stats.pending?.count || 0}</p>
                  <p className="text-sm text-yellow-600">Pending</p>
                  <p className="text-xs text-yellow-600">${stats.pending?.amount?.toFixed(2) || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-800">{stats.approved?.count || 0}</p>
                  <p className="text-sm text-blue-600">Approved</p>
                  <p className="text-xs text-blue-600">${stats.approved?.amount?.toFixed(2) || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-green-800">{stats.paid?.count || 0}</p>
                  <p className="text-sm text-green-600">Paid</p>
                  <p className="text-xs text-green-600">${stats.paid?.amount?.toFixed(2) || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <XCircle className="h-8 w-8 text-red-600" />
                <div>
                  <p className="text-2xl font-bold text-red-800">{stats.rejected?.count || 0}</p>
                  <p className="text-sm text-red-600">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter and Bulk Actions */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Withdrawals</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        {isSomeSelected && (
          <div className="flex items-center gap-2 bg-primary/5 px-4 py-2 rounded-lg border border-primary/20">
            <Badge variant="secondary" className="mr-2">
              {selectedIds.length} selected
            </Badge>
            {selectedPending.length > 0 && (
              <>
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setBulkApproveDialogOpen(true)}
                  disabled={bulkActionLoading}
                  data-testid="bulk-approve-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Approve ({selectedPending.length})
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => setBulkRejectDialogOpen(true)}
                  disabled={bulkActionLoading}
                  data-testid="bulk-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject ({selectedPending.length})
                </Button>
              </>
            )}
            {selectedApproved.length > 0 && (
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setBulkPaidDialogOpen(true)}
                disabled={bulkActionLoading}
                data-testid="bulk-paid-btn"
              >
                <Send className="h-4 w-4 mr-1" />
                Mark Paid ({selectedApproved.length})
              </Button>
            )}
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => setSelectedIds([])}
            >
              Clear
            </Button>
          </div>
        )}
        
        <p className="text-sm text-muted-foreground">
          Showing {withdrawals.length} withdrawal{withdrawals.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Withdrawals List */}
      {withdrawals.length > 0 ? (
        <div className="space-y-4">
          {withdrawals.map((withdrawal) => {
            const isSelectable = withdrawal.status === "pending" || withdrawal.status === "approved";
            return (
              <Card key={withdrawal.withdrawal_id} className={`card-organic ${selectedIds.includes(withdrawal.withdrawal_id) ? "ring-2 ring-primary" : ""}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {isSelectable && (
                        <Checkbox
                          checked={selectedIds.includes(withdrawal.withdrawal_id)}
                          onCheckedChange={() => toggleSelect(withdrawal.withdrawal_id)}
                          className="mt-3"
                          data-testid={`select-${withdrawal.withdrawal_id}`}
                        />
                      )}
                      <div className={`p-3 rounded-lg ${
                        withdrawal.status === 'pending' ? 'bg-yellow-100' :
                        withdrawal.status === 'approved' ? 'bg-blue-100' :
                        withdrawal.status === 'paid' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {getStatusIcon(withdrawal.status)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-lg">${withdrawal.amount?.toFixed(2)}</p>
                          <Badge className={getStatusBadge(withdrawal.status)}>{withdrawal.status}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span>{withdrawal.user?.full_name || withdrawal.user?.email || "Unknown"}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building className="h-3 w-3" />
                          <span>{withdrawal.bank_name} ****{withdrawal.account_number}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Requested: {new Date(withdrawal.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewWithdrawalDetails(withdrawal.withdrawal_id)}
                        data-testid={`view-${withdrawal.withdrawal_id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                      {withdrawal.status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            viewWithdrawalDetails(withdrawal.withdrawal_id);
                            setProcessForm({ ...processForm, status: 'approved' });
                          }}
                          data-testid={`approve-${withdrawal.withdrawal_id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      {withdrawal.status === 'approved' && (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => {
                            viewWithdrawalDetails(withdrawal.withdrawal_id);
                            setProcessForm({ ...processForm, status: 'paid' });
                          }}
                          data-testid={`mark-paid-${withdrawal.withdrawal_id}`}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Mark as Paid
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="card-organic">
          <CardContent className="p-12 text-center text-muted-foreground">
            <Banknote className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p>No withdrawal requests found</p>
          </CardContent>
        </Card>
      )}

      {/* Detail & Process Dialog */}
      <Dialog open={!!selectedWithdrawal} onOpenChange={(open) => !open && setSelectedWithdrawal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              <Banknote className="inline h-5 w-5 mr-2 text-primary" />
              Withdrawal Details
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : selectedWithdrawal && (
            <div className="space-y-6 pt-4">
              {/* Amount & Status */}
              <div className="bg-muted p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-foreground">${selectedWithdrawal.amount?.toFixed(2)}</p>
                <Badge className={getStatusBadge(selectedWithdrawal.status) + " mt-2"}>
                  {selectedWithdrawal.status}
                </Badge>
              </div>
              
              {/* Customer Info */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Customer</p>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="font-medium">{selectedWithdrawal.user?.full_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedWithdrawal.user?.email}</p>
                  {selectedWithdrawal.user?.phone && (
                    <p className="text-sm text-muted-foreground">{selectedWithdrawal.user.phone}</p>
                  )}
                </div>
              </div>
              
              {/* Bank Details */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Bank Details</p>
                <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bank</span>
                    <span className="font-medium">{selectedWithdrawal.bank_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account Holder</span>
                    <span className="font-medium">{selectedWithdrawal.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account Number</span>
                    <span className="font-mono font-medium">{selectedWithdrawal.account_number_full || `****${selectedWithdrawal.account_number}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Routing Number</span>
                    <span className="font-mono font-medium">{selectedWithdrawal.routing_number}</span>
                  </div>
                </div>
              </div>
              
              {selectedWithdrawal.notes && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Customer Notes</p>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg">{selectedWithdrawal.notes}</p>
                </div>
              )}
              
              {/* Process Actions */}
              {selectedWithdrawal.status !== 'paid' && selectedWithdrawal.status !== 'rejected' && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3">Process Withdrawal</p>
                  
                  <div className="space-y-3">
                    <div>
                      <Label>Action</Label>
                      <Select value={processForm.status} onValueChange={(val) => setProcessForm({...processForm, status: val})}>
                        <SelectTrigger data-testid="action-select">
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedWithdrawal.status === 'pending' && (
                            <>
                              <SelectItem value="approved">Approve</SelectItem>
                              <SelectItem value="rejected">Reject</SelectItem>
                            </>
                          )}
                          {selectedWithdrawal.status === 'approved' && (
                            <SelectItem value="paid">Mark as Paid</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {processForm.status === 'paid' && (
                      <div>
                        <Label>Transaction Reference</Label>
                        <Input
                          placeholder="e.g., Bank transfer ID"
                          value={processForm.transaction_reference}
                          onChange={(e) => setProcessForm({...processForm, transaction_reference: e.target.value})}
                          data-testid="transaction-ref"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label>Admin Notes</Label>
                      <Input
                        placeholder={processForm.status === 'rejected' ? "Reason for rejection" : "Optional notes"}
                        value={processForm.admin_notes}
                        onChange={(e) => setProcessForm({...processForm, admin_notes: e.target.value})}
                        data-testid="admin-notes"
                      />
                    </div>
                    
                    <Button
                      onClick={processWithdrawal}
                      disabled={processing || !processForm.status}
                      className={`w-full ${
                        processForm.status === 'approved' ? 'bg-green-600 hover:bg-green-700' :
                        processForm.status === 'paid' ? 'bg-blue-600 hover:bg-blue-700' :
                        processForm.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''
                      }`}
                      data-testid="process-btn"
                    >
                      {processing ? "Processing..." : `${processForm.status ? processForm.status.charAt(0).toUpperCase() + processForm.status.slice(1) : 'Select Action'}`}
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedWithdrawal.transaction_reference && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">Transaction Reference</p>
                  <p className="font-mono text-green-700">{selectedWithdrawal.transaction_reference}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Approve Dialog */}
      <Dialog open={bulkApproveDialogOpen} onOpenChange={setBulkApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve {selectedPending.length} Withdrawals?</DialogTitle>
            <DialogDescription>
              This will approve all selected pending withdrawals.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Admin Notes (Optional)</Label>
            <Textarea
              placeholder="Add notes for all approved withdrawals..."
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkApproveDialogOpen(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleBulkApprove} disabled={bulkActionLoading} data-testid="confirm-bulk-approve">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Approve All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reject Dialog */}
      <Dialog open={bulkRejectDialogOpen} onOpenChange={setBulkRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {selectedPending.length} Withdrawals?</DialogTitle>
            <DialogDescription>
              This will reject all selected pending withdrawals and refund the amounts to user wallets.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Rejection Reason</Label>
            <Textarea
              placeholder="Reason for rejection..."
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkReject} disabled={bulkActionLoading} data-testid="confirm-bulk-reject">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
              Reject All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Mark Paid Dialog */}
      <Dialog open={bulkPaidDialogOpen} onOpenChange={setBulkPaidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark {selectedApproved.length} Withdrawals as Paid?</DialogTitle>
            <DialogDescription>
              This will mark all selected approved withdrawals as paid.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Transaction Reference</Label>
            <Input
              placeholder="e.g., Batch transfer ID"
              value={bulkTransactionRef}
              onChange={(e) => setBulkTransactionRef(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPaidDialogOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleBulkMarkPaid} disabled={bulkActionLoading} data-testid="confirm-bulk-paid">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Mark All Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
